import { SxProps, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { Dayjs } from 'dayjs'
import { CSSProperties, Fragment, useMemo } from 'react'

import { helper } from 'helper'
import { useIsHoliday } from 'store/holidaySlice'
import { WorkPattern } from 'types/workPattern'

interface Props {
  summary: {
    date: Dayjs
    workPattern: WorkPattern
    count: number
  }[]
}

const isShortage = (count: number, require: number) => {
  return count < require
}

export default function AssignCountCellComponent(props: Props) {
  const isHoliday = useIsHoliday()
  const isShow = (workPattern: WorkPattern, date: Dayjs) => {
    return (
      workPattern.daysOfWeeks[helper.day.dayIndex(date)] ||
      (workPattern.daysOfWeeks[helper.week.WeekIndex.Hol] && isHoliday(date))
    )
  }

  // NOTE: 他でも使うようになったら共通化したい
  const getAssignNum = (workPattern: WorkPattern, date: Dayjs) => {
    if (isShow(workPattern, date)) {
      return workPattern.assignNum
    } else {
      return 0
    }
  }

  const sum = {
    need: props.summary.reduce(
      (s, v) => s + getAssignNum(v.workPattern, v.date),
      0,
    ),
    count: props.summary.reduce((s, v) => s + v.count, 0),
  }

  const row = useMemo(() => {
    return props.summary
      .filter(
        (v) => isShow(v.workPattern, v.date) && v.workPattern.assignNum > 0,
      )
      .map((v) => {
        const assign = getAssignNum(v.workPattern, v.date)
        return {
          id: v.workPattern.id,
          name: v.workPattern.name,
          count: v.count,
          assign: assign,
          isShortage: isShortage(v.count, assign),
        }
      })
  }, [props.summary])

  return (
    <Box style={containerStyle}>
      <Box>
        {row.map((v) => (
          <Fragment key={`row-${v.id}`}>
            <Box
              sx={
                {
                  ...rowContainerStyle,
                  ...(v.isShortage && alertBGStyle),
                } as SxProps
              }
            >
              <Box>
                <Box style={workPatternTextStyle}>{v.name}</Box>
              </Box>
              <Box>
                <Box style={counterStyle}>
                  <Typography component={'span'} style={counterTextStyle}>
                    {v.count}
                  </Typography>
                  <Typography component={'span'} style={counterSeparateStyle}>
                    /
                  </Typography>
                  <Typography
                    component={'span'}
                    sx={
                      {
                        ...counterTextStyle,
                        ...(v.isShortage && alertTextStyle),
                      } as SxProps
                    }
                  >
                    {v.assign}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box style={rowSeparateStyle}>{/*border*/}</Box>
          </Fragment>
        ))}
      </Box>

      <Box style={containerSpacerStyle}>{/*余白*/}</Box>

      <Box
        sx={
          {
            ...rowContainerStyle,
            ...(isShortage(sum.count, sum.need) && alertBGStyle),
          } as SxProps
        }
      >
        <Box>計</Box>
        <Box>
          <Box style={counterStyle}>
            <Typography component={'span'} style={counterTextStyle}>
              {sum.count}
            </Typography>
            <Typography component={'span'} style={counterSeparateStyle}>
              /
            </Typography>
            <Typography
              component={'span'}
              sx={
                {
                  ...counterTextStyle,
                  ...(isShortage(sum.count, sum.need) && alertTextStyle),
                } as SxProps
              }
            >
              {sum.need}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}

const containerSpacerStyle: CSSProperties = {
  flex: 1,
}

const rowContainerStyle: SxProps = {
  display: 'flex',
  width: '100%',
  gap: '2px',
  padding: '3px 8px',
  boxSizing: 'border-box',
  '& > *': {
    flex: 1,
    overflow: 'hidden',
  },

  // NOTE: tdセルに合わせてwidthを指定
  //       セル幅に自動で合わせる方法が不明だったので、一旦固定値で指定
  maxWidth: '105px',
}

const alertBGStyle: SxProps = {
  backgroundColor: '#FFE1EB',
}

const alertTextStyle: SxProps = {
  color: '#FF005A',
}

const rowSeparateStyle: CSSProperties = {
  width: '100%',
  borderBottom: '1px dashed #D9D9D9',
}

const workPatternTextStyle: CSSProperties = {
  backgroundColor: 'white',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '0 4px',
  fontSize: '12px',
  border: '1px solid #B4BEBE',
  borderRadius: '4px',
  maxWidth: '100%',
  width: 'fit-content',
}

const counterStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-evenly',
  alignItems: 'center',
  padding: '0 4px',
  border: '1px solid #B4BEBE',
  borderRadius: '4px',
  height: '100%',
  backgroundColor: 'white',
}

const counterTextStyle: CSSProperties = {
  flex: 1,
  textAlign: 'center',
  fontSize: '12px',
}

const counterSeparateStyle: CSSProperties = {
  fontSize: '12px',
  color: '#B4BEBE',
}
